import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  Row,
  Col,
  Typography,
  Button,
  ModalTrigger,
  ChartPie,
  ChartConfig,
  onCloseModal,
} from '@repo/ui/components'
import { ButtonLink } from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import { ValueWithUnit, PaymentType } from '@repo/shared/components'
import * as React from 'react'
import {
  UserAccountPortfolio,
  UserProject,
  useCheckBuyoutEligibility,
} from '@repo/services'
import { FeatureFlag } from '@repo/shared/components'

interface DeliverDialogProps {
  project: UserProject
  userProject: UserAccountPortfolio
}

const formatNumberWithCommas = (
  _value: number | string | undefined,
): string => {
  if (_value === undefined || _value === null || _value === '') return ''

  let value = _value.toString()

  if (value.startsWith('.')) value = '0' + value

  const [firstPart = '', decimalPart] = value.split('.')
  const formattedInt = firstPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  if (decimalPart) {
    const trimmedDecimal = decimalPart.slice(0, 4).replace(/0+$/, '')
    return trimmedDecimal ? `${formattedInt}.${trimmedDecimal}` : formattedInt
  }

  return formattedInt
}

const getPercentage = (percentage: number) => {
  return formatNumberWithCommas((percentage * 100) / 100)
}

export const DeliverDialog: React.FC<DeliverDialogProps> = ({
  project,
  userProject,
}) => {
  const { t } = useTranslate()
  const { data: checkBuyoutEligibility } = useCheckBuyoutEligibility({
    token: project.token || '',
  })

  const [open, setOpen] = React.useState(false)
  const totalTokens = checkBuyoutEligibility?.data?.total || 0
  const yourAssetsTokens = checkBuyoutEligibility?.data?.yours || 0
  const boughtAssetsTokensValue = checkBuyoutEligibility?.data?.bought || 0
  const otherOwnersTokens = +(boughtAssetsTokensValue - yourAssetsTokens)
  const freeTokens = checkBuyoutEligibility?.data?.available || 0
  const freeTokensValue = project.price?.priceRange24h?.last

  const yourAssetsTokensPercentage = getPercentage(
    (yourAssetsTokens / totalTokens) * 100,
  )

  const otherOwnersTokensPercentage = getPercentage(
    (otherOwnersTokens / totalTokens) * 100,
  )
  const freeTokensPercentage = getPercentage((freeTokens / totalTokens) * 100)

  const pieChartData = [
    {
      name: 'yourAssetsTokens',
      value: yourAssetsTokens,
      fill: 'var(--color-neutral-surface-80)',
    },
    {
      name: 'otherOwnersTokens',
      value: otherOwnersTokens,
      fill: '#F2CCD9',
    },
    {
      name: 'freeTokens',
      value: freeTokens,
      fill: '#E9E9E9',
    },
  ]

  const chartConfig = {
    value: {
      label: '',
    },
    yourAssetsTokens: {
      label: t('deliverDialog_your_assets'),
      color: 'var(--color-neutral-surface-80)',
    },
    otherOwnersTokens: {
      label: t('deliverDialog_other_owners'),
      color: '#F2CCD9',
    },
    freeTokens: {
      label: t('deliverDialog_free_tokens'),
      color: '#E9E9E9',
    },
  } satisfies ChartConfig

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalTrigger asChild>
        <FeatureFlag flagKey='project-delivery'>
          <Button
            onClick={() => setOpen(true)}
            variant='fill'
            color='base'
            size='xs'
            className='w-1/2'
          >
            {t('walletPage_project_deliver_action')}
          </Button>
        </FeatureFlag>
      </ModalTrigger>
      <ModalContent className='md:max-w-[720px]'>
        <ModalHeader>
          <ModalTitle>
            <Typography size='md' weight='bold'>
              {t('deliverDialog_title')}
            </Typography>
          </ModalTitle>
        </ModalHeader>
        <Col className='gap-md  max-h-[550px] overflow-y-auto'>
          <Typography
            size='md'
            color='text-neutral-main'
            className='text-center'
          >
            {t('deliverDialog_description')}
          </Typography>

          {project && userProject && (
            <ChartPie
              data={pieChartData}
              config={chartConfig}
              showLegend={false}
              className='aspect-square max-h-[96px]'
            />
          )}

          <Row className='justify-between'>
            <Typography size='sm' weight='bold' className='text-center'>
              {t('deliverDialog_total_tokens')}
            </Typography>
            <ValueWithUnit
              value={project.market?.totalSupply || 0}
              unit='crypto'
              valueSize='md'
              unitSize='xs'
              scale={project.scale}
              valueWeight='bold'
            />
          </Row>

          <Row className='justify-between'>
            <Typography
              size='sm'
              className='text-center flex items-center gap-xs'
              color='text-neutral-surface-80'
            >
              <span className='flex h-sm w-sm bg-base-main rounded-full' />
              <span>{yourAssetsTokensPercentage}%</span>
              {t('deliverDialog_your_assets')}
            </Typography>

            <ValueWithUnit
              value={yourAssetsTokens}
              unit='crypto'
              valueSize='md'
              unitSize='xs'
              valueWeight='regular'
              scale={project.scale}
            />
          </Row>
          <Row className='justify-between'>
            <Typography
              size='sm'
              className='text-center flex items-center gap-xs'
              color='text-neutral-surface-80'
            >
              <span className='flex h-sm w-sm bg-[#F2CCD9] rounded-full' />
              <span>{otherOwnersTokensPercentage}%</span>
              {t('deliverDialog_other_owners')}
            </Typography>
            <ValueWithUnit
              value={otherOwnersTokens}
              unit='crypto'
              valueSize='md'
              unitSize='xs'
              valueWeight='regular'
              scale={project.scale}
            />
          </Row>
          <Row className='justify-between'>
            <Typography
              size='sm'
              className='text-center flex items-center gap-xs'
              color='text-neutral-surface-80'
            >
              <span className='flex h-sm w-sm bg-[#E9E9E9] rounded-full' />
              <span>{freeTokensPercentage}%</span>
              {t('deliverDialog_free_tokens')}
            </Typography>
            <ValueWithUnit
              value={freeTokens}
              unit='crypto'
              valueSize='md'
              unitSize='xs'
              valueWeight='regular'
              scale={project.scale}
            />
          </Row>

          <Col className='bg-positive-surface border border-positive-surface-20 p-md rounded-sm  gap-md'>
            <Typography
              size='xs'
              weight='bold'
              color='text-positive-surface-100'
            >
              {t('deliverDialog_buy_free_tokens')}
            </Typography>
            <Typography
              size='sm'
              weight='regular'
              color='text-positive-surface-100'
            >
              {t('deliverDialog_buy_free_tokens_description')}
            </Typography>
            <Row className='justify-between'>
              <Typography
                size='sm'
                className='text-center flex items-center'
                color='text-neutral-surface-80'
              >
                {t('deliverDialog_free_tokens')}
              </Typography>
              <ValueWithUnit
                value={freeTokens}
                unit='crypto'
                valueSize='md'
                unitSize='xs'
                valueWeight='regular'
                scale={project.scale}
              />
            </Row>
            <Row className='justify-between'>
              <Typography
                size='sm'
                className='text-center flex items-center'
                color='text-neutral-surface-80'
              >
                {t('deliverDialog_approximate_value')}
              </Typography>
              <PaymentType
                value={freeTokensValue}
                isBold={false}
                numberSize='md'
                size='xs'
              />
            </Row>
            <Row className='justify-between'>
              <FeatureFlag flagKey='advanced-trade'>
                <ButtonLink
                  href={`/advanced-trade?token=${project.token}`}
                  variant='transparent'
                  color='positive'
                  width='full'
                  size='sm'
                  className='min-w-1/2 w-full'
                >
                  {t('deliverDialog_buy_in_advanced_trade')}
                </ButtonLink>
              </FeatureFlag>
              <FeatureFlag flagKey='quick-trade'>
                <ButtonLink
                  href={`/quick-trade?projectId=${project.token}&type=buy`}
                  variant='fill'
                  color='positive'
                  width='full'
                  size='sm'
                  className='min-w-1/2 w-full'
                >
                  {t('deliverDialog_buy_in_market')}
                </ButtonLink>
              </FeatureFlag>
            </Row>
          </Col>
        </Col>

        <ModalFooter>
          <Button
            variant='ghost'
            color='neutral'
            width='full'
            size='md'
            onClick={onCloseModal}
          >
            {t('deliverDialog_back')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
